import { yupResolver } from '@hookform/resolvers/yup';
import { ReactComponent as Logo } from 'assets/images/registration/logo.svg';
import { ReactComponent as EyeIcon } from 'assets/images/icon/eye-icon.svg';
import { AxiosError, AxiosResponse } from 'axios';
import { Button } from 'components/universal/button';
import Heading from 'components/universal/heading';
import { Input } from 'components/universal/input';
import Paragraph from 'components/universal/paragraph';
import { QUERY_KEYS } from 'constants/query-keys';
import { PATHS, ROUTES } from 'constants/routes';
import { useUser } from 'hooks/useUser';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getUserData, signIn } from 'services/registration';
import { APIErrors } from 'types/api';
import { SignInDTO } from 'types/user';
import { queryKeyWithLanguage } from 'utils/language';
import { setToken } from 'utils/local-storage-token';
import cls from './login.module.scss';
import { schema } from './validation';
import { useState } from 'react';

interface LocationState {
    from: string;
}
export interface ILoginFormValues {
    login: string;
    password: string;
}

export const LoginView = () => {
    const location = useLocation();
    const state = location.state as LocationState;
    const navigate = useNavigate();
    const { setUserData } = useUser();
    const { t } = useTranslation();
    const [showPassword, isShowPassword] = useState<boolean>(false);

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<ILoginFormValues>({
        resolver: yupResolver(schema),
        defaultValues: {
            login: '',
            password: '',
        },
    });

    const onSubmit = (DTO) => signInMutation.mutate(DTO);

    const showPasswordHandle = () => {
        isShowPassword(!showPassword);
    };

    const { refetch: refetchUserData } = useQuery<AxiosResponse, AxiosError>(
        queryKeyWithLanguage(QUERY_KEYS.GET_USER_DATA),
        {
            queryFn: () => getUserData(),
            enabled: false,
        }
    );

    const signInMutation = useMutation<
        AxiosResponse<{ token: string }>,
        AxiosError<APIErrors>,
        SignInDTO
    >((data) => signIn(data), {
        onSuccess: async (data) => {
            setToken(data.data.token);

            const res = await refetchUserData();
            const user = res.data?.data;

            setUserData(user);

            if (!user?.first_name || !user?.last_name) {
                navigate(PATHS.COMPLETE_PROFILE);
                return;
            }

            navigate(state?.from || PATHS.HOME);
        },
    });

    return (
        <div className={cls.container}>
            <div className={cls.columnLeft}>
                <Heading
                    className={cls.heading}
                    headingLevel="h1"
                    headingStyle="h2"
                >
                    {t('logIn.title')}
                </Heading>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Input
                        className="mb-11"
                        register={register}
                        id="login"
                        name="login"
                        label={t('logIn.login')}
                        error={t(errors.login?.message)}
                        maxLength={255}
                    />
                    <div className={cls.inputPasswordWrapper}>
                        <Input
                            className={cls.inputPassword}
                            register={register}
                            id="password"
                            name="password"
                            label={t('logIn.password')}
                            error={t(errors.password?.message)}
                            type={!showPassword ? 'password' : 'text'}
                            maxLength={255}
                        />
                        <div
                            className={cls.showPasswordStyle}
                            onClick={showPasswordHandle}
                        >
                            <EyeIcon />
                        </div>
                    </div>
                    <Link
                        className={cls.linkNotRemember}
                        to={ROUTES.RESET_PASSWORD_PAGE.path}
                    >
                        {t('logIn.' + ROUTES.RESET_PASSWORD_PAGE.name)}
                    </Link>
                    <Button className="mx-auto mb-5" type="submit">
                        {t('logIn.logIn')}
                    </Button>
                    <Paragraph className="text-center">
                        {t('logIn.registerText')}{' '}
                        <Link
                            className={cls.registerLink}
                            to={ROUTES.REGISTER_CHOOSE.path}
                        >
                            {t('logIn.' + ROUTES.REGISTRATION.name)}
                        </Link>
                    </Paragraph>
                </form>
            </div>
            <Logo className={cls.image} />
        </div>
    );
};
